import { Sage100CompensationsExportFeatureDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'
// TODO: Migrate to GraphQL legal entities. Table/codeables still expect mobx collection.
import legalEntities from 'modules/core/store/collections/legalEntities' // eslint-disable-line factorial/no-allowed-mobx-collections

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import { useGetMarketplaceCompanyInstallations } from 'modules/marketplace/hooks/integrations/companyInstallations/useGetMarketplaceCompanyInstallations'
import integrationCodes from 'modules/marketplace/lib/integrationCodes'
import { useConcepts } from 'modules/payroll_bundle/hooks/useConcepts'
import { useHasCompensations } from 'modules/payroll_bundle/hooks/useHasCompensations'
import { buildPayrollConceptsByLegalEntitySettings } from 'modules/payroll_bundle/lib/settingsPayrollConcept'
import PayrollPaywallGuard from 'modules/payroll_bundle/pages/PayrollPaywallGuard'
import EmployeesData from 'modules/payroll_integrations/components/Content/EmployeeData'
import Table from 'modules/payroll_integrations/components/Table'
import PayrollIntegration from 'modules/payroll_integrations/lib/catalog'
import type { Codeable, ColumnNames } from 'modules/payroll_integrations/lib/types'

const Sage100Integration = () => {
  const { company } = useCurrentSession()

  const INTEGRATION_NAME = PayrollIntegration.sage100

  const COLUMN_NAMES: ColumnNames = [
    {
      name: i18n.t(
        'app_store_v2.app_integrations.payroll_integrations_sage100.settings.form.header.concept'
      ),
      colSpan: 4,
      attribute: 'concept',
    },
    {
      name: i18n.t(
        'app_store_v2.app_integrations.payroll_integrations_sage100.settings.form.header.value'
      ),
      colSpan: 4,
      attribute: 'value',
    },
  ]

  const { hasCompensations, loading: loadingHasCompensations } = useHasCompensations()

  const { data: featureFlagData, loading: loadingFeatureFlag } = useQuery(
    Sage100CompensationsExportFeatureDocument,
    {
      variables: { companyId: company.id },
    }
  )

  const hasSage100CompensationsExport =
    featureFlagData?.features?.featuresConnection?.nodes?.[0]?.enabled ?? false

  const { concepts: payrollConcepts, loading: loadingConcepts } = useConcepts(
    undefined,
    [
      'deductions',
      'earnings_benefits_in_kind',
      'earnings_fixed_salary',
      'earnings_others',
      'earnings_variable',
    ],
    true,
    !hasCompensations
  )

  const { data: companyInstallationsData, loading: companyInstallationsDataLoading } =
    useGetMarketplaceCompanyInstallations(
      {
        integrationDatoIds: [integrationCodes.sage100],
      },
      !hasCompensations
    )

  const sage100Installation = companyInstallationsData[0]

  const payrollConceptsByLegalEntity = buildPayrollConceptsByLegalEntitySettings(
    payrollConcepts,
    legalEntities
  )

  const codeables: Record<string, Codeable> = {
    legalEntity: {
      position: 0,
      collection: legalEntities,
      class: 'LegalEntity',
      label: i18n.t(
        'app_store_v2.app_integrations.payroll_integrations_sage100.settings.form.concept.legal_entity'
      ),
    },
  }

  if (hasCompensations && hasSage100CompensationsExport) {
    codeables.payrollConcepts = {
      position: 1,
      collection: payrollConceptsByLegalEntity || [],
      class: 'Payroll::Concept',
      label: i18n.t(
        'app_store_v2.app_integrations.payroll_integrations_a3nom.settings.form.payroll_concept'
      ),
    }
  }

  return (
    <PayrollPaywallGuard>
      <ResponsivePage.Body
        variant="full"
        loading={
          loadingHasCompensations ||
          loadingFeatureFlag ||
          loadingConcepts ||
          companyInstallationsDataLoading
        }
        error={null}
      >
        <Table
          columnNames={COLUMN_NAMES}
          codeables={codeables}
          integration={INTEGRATION_NAME}
          integrationInstallation={sage100Installation}
        />
        <EmployeesData
          integration_name={i18n.t(
            'app_store_v2.app_integrations.payroll_integrations_sage100.integration_name'
          )}
        />
      </ResponsivePage.Body>
    </PayrollPaywallGuard>
  )
}

export default Sage100Integration
